import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { ChevronLeft, Copy, Loader2 } from "lucide-react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { normalizeProgramType } from "../lib/programCreation";

const PROGRAM_TYPES = [
  {
    id: "recreational",
    label: "Community program",
    icon: "🎮",
    description: "Classes, academies, training, open gyms, and other participant-enrollment activities",
    features: ["Participant enrollment", "Flexible groups", "Recurring or date-ranged"],
    path: "/admin/programs/new/recreational",
  },
  {
    id: "competitive",
    label: "Tryout or evaluation",
    icon: "🏆",
    description: "Tryouts, combines, showcases, and evaluations that can feed a future league roster",
    features: ["Evaluation details", "Participant selection", "Follow-up communication"],
    path: "/admin/programs/new/competitive",
  },
  {
    id: "camp",
    label: "Camp, clinic, or workshop",
    icon: "⛺",
    description: "One-time or multi-session offerings organized around capacity and activity groups",
    features: ["Groups, not teams", "Session-based", "Capacity and check-in"],
    path: "/admin/programs/new/camp",
  },
];

const ProgramTypeSelector = ({ embedded = false, routeBase = "/admin/programs" }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [recentPrograms, setRecentPrograms] = useState([]);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [loadingRecentPrograms, setLoadingRecentPrograms] = useState(Boolean(token));

  useEffect(() => {
    if (!token) return;
    const loadRecentPrograms = async () => {
      try {
        const response = await axios.get(`${API}/programs`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { page: 1, page_size: 12 },
        });
        const programs = response.data?.items || response.data || [];
        setRecentPrograms(programs
          .filter((program) => ["recreational", "competitive", "camp"].includes(normalizeProgramType(program.program_type)))
          .slice(0, 4));
      } catch {
        setRecentPrograms([]);
      } finally {
        setLoadingRecentPrograms(false);
      }
    };
    loadRecentPrograms();
  }, [token]);

  const duplicateProgram = async (program) => {
    setDuplicatingId(program.program_id);
    try {
      const response = await axios.post(`${API}/programs/${program.program_id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reset_schedule: true },
      });
      toast.success("Previous program copied as a private draft");
      navigate(`${routeBase}/${response.data.program_id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not reuse this program");
    } finally {
      setDuplicatingId(null);
    }
  };

  const content = (
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-12">
        {/* Header */}
        <div className="mb-8 rounded-[28px] border border-[#d8dfd9] bg-[#fbfcf8] p-6 shadow-[0_18px_50px_rgba(38,49,47,0.08)] sm:p-8">
          <button
            onClick={() => navigate(routeBase)}
            className="mb-5 flex items-center gap-1 text-sm font-medium text-[#66736f] transition-colors hover:text-[#16877c]"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Programs
          </button>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#16877c]">Program builder</p>
          <h1 className="text-3xl font-bold tracking-tight text-[#101a19] sm:text-4xl">What kind of program are you offering?</h1>
          <p className="mt-2 max-w-3xl text-[#66736f]">Programs enroll people into an activity. If you need standings, playoffs, pools, or brackets, create it from the Leagues or Tournaments module instead.</p>
        </div>

        {(loadingRecentPrograms || recentPrograms.length > 0) && (
          <section className="mb-10 rounded-[24px] border border-[#b9d8d2] bg-[#eef8f5] p-5 sm:p-6" aria-labelledby="reuse-program-heading" aria-busy={loadingRecentPrograms}>
            <div className="mb-4">
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#16877c]">Fastest for returning programs</p>
              <h2 id="reuse-program-heading" className="text-xl font-bold text-[#101a19]">Copy a previous program and update the dates</h2>
              <p className="mt-1 text-sm text-[#53625e]">Start with its divisions, pricing, documents, add-ons, and email settings. The copy stays private until you review and publish it.</p>
            </div>
            {loadingRecentPrograms ? (
              <div className="flex min-h-20 items-center justify-center rounded-2xl border border-[#d8dfd9] bg-white/80" role="status">
                <Loader2 className="mr-2 h-4 w-4 animate-spin text-[#16877c]" aria-hidden="true" />
                <span className="text-sm text-[#53625e]">Loading recent programs…</span>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {recentPrograms.map((program) => (
                  <div key={program.program_id} className="flex items-center justify-between gap-4 rounded-2xl border border-[#d8dfd9] bg-white p-4">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-gray-900">{program.name}</p>
                      <p className="text-xs text-gray-500">{program.sport} · {normalizeProgramType(program.program_type) === "camp" ? "Camp" : normalizeProgramType(program.program_type)}</p>
                    </div>
                    <button type="button" onClick={() => duplicateProgram(program)} disabled={Boolean(duplicatingId)} className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl border border-[#bfcac2] bg-white px-3 py-2 text-sm font-semibold text-[#26312f] hover:border-[#16877c] hover:text-[#116d64] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16877c] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" aria-label={`Copy ${program.name} and update the dates`}>
                      {duplicatingId === program.program_id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
                      Copy
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <div className="mb-4">
          <h2 className="text-xl font-bold text-[#101a19]">Or start from scratch</h2>
          <p className="mt-1 text-sm text-[#66736f]">Choose the structure that best matches how participants will enroll.</p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PROGRAM_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => navigate(`${routeBase}/new/${type.id}`)}
              className="group min-h-60 rounded-[24px] border border-[#d8dfd9] bg-[#fbfcf8] p-6 text-left transition-all hover:-translate-y-0.5 hover:border-[#16877c] hover:shadow-[0_18px_40px_rgba(22,135,124,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#16877c] focus-visible:ring-offset-2"
            >
              <div className="text-3xl mb-3">{type.icon}</div>
              <h2 className="mb-1 text-lg font-bold text-[#101a19] group-hover:text-[#116d64]">
                {type.label}
              </h2>
              <p className="mb-4 text-sm text-[#66736f]">{type.description}</p>
              <ul className="space-y-1">
                {type.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-xs text-[#66736f]">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#9bc53d]" />
                    {f}
                  </li>
                ))}
              </ul>
            </button>
          ))}
        </div>

      </div>
  );
  return embedded ? content : <AdminLayout>{content}</AdminLayout>;
};

export default ProgramTypeSelector;
